import { OrderedItem, OrderedItemModifier, OrderedItemTax } from '../interfaces';
import { ModifierGroup, TaxCodeData } from '../models';

export default class TaxCalculator {
  /**
   * @description
   * This calculates the tax amount for a price that includes tax give a tax rate
   */
  static calculateTaxIncludedTaxAmount(price: number, taxRate: number): number {
    return Math.ceil((price * taxRate) / (1 + taxRate));
  }

  /**
   * @description
   * Get the tax amount for a price that excludes tax given a tax rate
   */
  static calculateTax(amount: number, taxRate: number): number {
    return Math.ceil(amount * taxRate);
  }

  static calculatePreTaxPriceFromTaxes(amount: number, taxes: TaxCodeData[]): number {
    // For tax-included prices, you need to work backwards
    // Calculate the pre-tax price by removing each tax sequentially
    let preTaxPrice = amount;
    (taxes || []).forEach(tax => {
      if (tax?.taxRate == null) {
        return;
      }
      preTaxPrice -= TaxCalculator.calculateTaxIncludedTaxAmount(preTaxPrice, tax.taxRate * 0.01);
    });
    return preTaxPrice;
  }

  static getItemTaxes(
    orderedItem: OrderedItem,
    taxes: TaxCodeData[],
    taxExempt = false
  ): OrderedItemTax[] {
    if (taxExempt) {
      return [];
    }
    const { taxIncluded, unitPrice, quantity, itemDiscountTotal } = orderedItem;
    const afterDiscountPrice = unitPrice * quantity - itemDiscountTotal;
    if (!afterDiscountPrice) {
      return [];
    }

    const validTaxes = (taxes || []).filter(tax => tax?._id != null && tax?.taxRate != null);
    const newPreTaxPrice = taxIncluded
      ? TaxCalculator.calculatePreTaxPriceFromTaxes(afterDiscountPrice, validTaxes)
      : afterDiscountPrice;

    return validTaxes.map(tax => {
      return {
        id: tax._id,
        total: TaxCalculator.calculateTax(newPreTaxPrice, tax.taxRate * 0.01),
      };
    });
  }

  static getModifierItemTaxes(
    modifier: OrderedItemModifier,
    itemQuantity: number,
    taxes: TaxCodeData[],
    taxExempt = false
  ): OrderedItemTax[] {
    if (taxExempt) {
      return [];
    }
    const { unitPrice, discountTotal, taxIncluded } = modifier;
    const afterDiscountPrice = unitPrice * itemQuantity - discountTotal;
    if (!afterDiscountPrice) {
      return [];
    }
    const validTaxes = (taxes || []).filter(tax => tax?._id != null && tax?.taxRate != null);
    const newPreTaxPrice = taxIncluded
      ? TaxCalculator.calculatePreTaxPriceFromTaxes(afterDiscountPrice, validTaxes)
      : afterDiscountPrice;

    const modTaxes = validTaxes.map(tax => {
      return {
        id: tax._id,
        total: TaxCalculator.calculateTax(newPreTaxPrice, tax.taxRate * 0.01),
      };
    });
    return modTaxes;
  }

  static getTaxTotal(taxes: OrderedItemTax[], taxExempt = false): number {
    if (taxExempt) {
      return 0;
    }
    return taxes.reduce((acc, tax) => acc + tax.total, 0);
  }

  static setTaxesForOrderedItemModifiers(
    orderedItem: OrderedItem,
    taxesById: Record<string, TaxCodeData>,
    modifierGroupsById: Record<string, ModifierGroup>,
    taxExempt = false
  ): void {
    orderedItem.modifierTaxIncludedTaxTotal = 0;
    orderedItem.modifiers.forEach(modifier => {
      const modifierGroup = modifierGroupsById[modifier.modifierSet];
      const modifierOption = modifierGroup?.data.options.find(
        option => option._id === modifier.modifierSetOption
      );
      const modifierTaxes = (modifierOption?.taxCodes || [])
        .map(taxId => taxesById[taxId])
        .filter(Boolean);
      modifier.taxes = TaxCalculator.getModifierItemTaxes(
        modifier,
        orderedItem.quantity,
        modifierTaxes,
        taxExempt
      );
      modifier.taxTotal = TaxCalculator.getTaxTotal(modifier.taxes, taxExempt);
      if (modifier.taxIncluded) {
        orderedItem.modifierTaxIncludedTaxTotal += modifier.taxTotal;
      }
    });
    orderedItem.modifierTaxTotal = orderedItem.modifiers.reduce(
      (acc, modifier) => acc + modifier.taxTotal,
      0
    );
  }

  static setTaxesForOrderedItem(
    orderedItem: OrderedItem,
    taxes: TaxCodeData[],
    taxExempt = false
  ): void {
    orderedItem.taxes = TaxCalculator.getItemTaxes(orderedItem, taxes, taxExempt);
    orderedItem.itemTaxTotal = TaxCalculator.getTaxTotal(orderedItem.taxes, taxExempt);
  }
}
